import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  getMyGroups,
  getAllGroups,
  getGroupMembers,
  getPendingRequests,
  updateMemberStatus,
  removeMember,
  createGroup,
  type GroupMembership,
  type GroupMember,
  type PendingRequest,
  type Group,
} from '../lib/api/groups';
import { getUsers, updateUserRole, type AdminUser, type Role } from '../lib/api/admin';

type Tab = 'requests' | 'members' | 'groups' | 'users';

function displayName(first?: string | null, last?: string | null, email?: string) {
  const name = [first, last].filter(Boolean).join(' ').trim();
  return name || email || 'Unknown';
}

export function Admin() {
  const { user } = useAuth();
  const isSuper = user?.role === 'superadmin';

  const [tab, setTab] = useState<Tab>('requests');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Requests
  const [requests, setRequests] = useState<PendingRequest[]>([]);
  // Groups I administer (group-admin)
  const [adminGroups, setAdminGroups] = useState<GroupMembership[]>([]);
  // Members tab
  const [selectedGroupId, setSelectedGroupId] = useState<string>('');
  const [members, setMembers] = useState<GroupMember[]>([]);
  // Groups tab
  const [allGroups, setAllGroups] = useState<Group[]>([]);
  const [newName, setNewName] = useState('');
  const [newDesc, setNewDesc] = useState('');
  // Users tab
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [userSearch, setUserSearch] = useState('');

  useEffect(() => {
    void refreshTop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function refreshTop() {
    try {
      const [reqs, my, all] = await Promise.all([
        getPendingRequests().catch(() => []),
        getMyGroups().catch(() => []),
        getAllGroups().catch(() => []),
      ]);
      setRequests(reqs);
      const admins = my.filter((g) => g.role === 'admin' && g.status === 'approved');
      setAdminGroups(admins);
      setAllGroups(all);
      if (!selectedGroupId && admins.length > 0) setSelectedGroupId(admins[0].id);
    } catch {
      setError('Failed to load admin data');
    }
  }

  async function loadMembers(groupId: string) {
    if (!groupId) return;
    try {
      setMembers(await getGroupMembers(groupId));
    } catch {
      setError('Failed to load members');
    }
  }

  useEffect(() => {
    if (tab === 'members' && selectedGroupId) void loadMembers(selectedGroupId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, selectedGroupId]);

  useEffect(() => {
    if (tab === 'users' && isSuper) void loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  async function loadUsers() {
    try {
      setUsers(await getUsers(userSearch || undefined));
    } catch {
      setError('Failed to load users');
    }
  }

  async function act<T>(fn: () => Promise<T>, after: () => void, ok?: string) {
    setError(null);
    setNotice(null);
    setBusy(true);
    try {
      await fn();
      if (ok) setNotice(ok);
      after();
    } catch (e: unknown) {
      const er = e as { response?: { data?: { error?: string } } };
      setError(er.response?.data?.error || 'Action failed');
    } finally {
      setBusy(false);
    }
  }

  const tabs: { id: Tab; label: string; show: boolean }[] = [
    { id: 'requests', label: `Requests${requests.length ? ` (${requests.length})` : ''}`, show: true },
    { id: 'members', label: 'Members', show: true },
    { id: 'groups', label: 'Groups', show: true },
    { id: 'users', label: 'Users', show: !!isSuper },
  ];

  return (
    <div className="h-full flex flex-col bg-background overflow-hidden">
      <div className="flex-shrink-0 border-b border-border bg-white px-6 py-4">
        <h1 className="text-2xl font-bold">Admin</h1>
        <p className="text-sm text-muted-foreground mt-1">Manage join requests, members, groups{isSuper ? ' and users' : ''}.</p>
        <div className="flex gap-1 mt-4">
          {tabs.filter((t) => t.show).map((t) => (
            <button
              key={t.id}
              onClick={() => { setTab(t.id); setError(null); setNotice(null); }}
              className={`px-4 py-2 rounded-lg text-sm font-medium ${tab === t.id ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-secondary'}`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-5xl mx-auto">
          {error && <div className="mb-4 p-3 bg-destructive/10 border border-destructive rounded-lg text-sm text-destructive">{error}</div>}
          {notice && <div className="mb-4 p-3 bg-green-500/10 border border-green-500 rounded-lg text-sm text-green-600">{notice}</div>}

          {/* REQUESTS */}
          {tab === 'requests' && (
            requests.length === 0 ? (
              <p className="text-muted-foreground">No pending join requests.</p>
            ) : (
              <div className="space-y-3">
                {requests.map((r) => (
                  <div key={`${r.groupId}_${r.userId}`} className="flex items-center justify-between p-4 bg-white border border-border rounded-lg">
                    <div>
                      <div className="font-semibold">{displayName(r.userFirstName, r.userLastName, r.userEmail)}</div>
                      <div className="text-sm text-muted-foreground">{r.userEmail} → wants to join <strong>{r.groupName}</strong></div>
                    </div>
                    <div className="flex gap-2">
                      <button disabled={busy} onClick={() => act(() => updateMemberStatus(r.groupId, r.userId, 'approved', 'member'), refreshTop, 'Approved')} className="px-3 py-1.5 text-sm bg-primary text-primary-foreground rounded-lg disabled:opacity-50">Approve</button>
                      <button disabled={busy} onClick={() => act(() => updateMemberStatus(r.groupId, r.userId, 'rejected'), refreshTop, 'Rejected')} className="px-3 py-1.5 text-sm border border-input rounded-lg disabled:opacity-50">Reject</button>
                    </div>
                  </div>
                ))}
              </div>
            )
          )}

          {/* MEMBERS */}
          {tab === 'members' && (
            adminGroups.length === 0 ? (
              <p className="text-muted-foreground">You don't administer any groups yet. Create one in the Groups tab.</p>
            ) : (
              <div>
                <select
                  value={selectedGroupId}
                  onChange={(e) => setSelectedGroupId(e.target.value)}
                  className="mb-4 px-4 py-2 border border-input rounded-lg"
                >
                  {adminGroups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
                </select>
                {members.length === 0 ? (
                  <p className="text-muted-foreground">No members yet.</p>
                ) : (
                  <div className="bg-white border border-border rounded-lg overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-secondary"><tr>
                        <th className="px-4 py-3 text-left text-sm font-semibold">Member</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold">Status</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold">Role</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold">Actions</th>
                      </tr></thead>
                      <tbody>
                        {members.map((m) => (
                          <tr key={m.userId} className="border-t border-border">
                            <td className="px-4 py-3 text-sm">
                              <div className="font-medium">{displayName(m.firstName, m.lastName, m.email)}</div>
                              <div className="text-xs text-muted-foreground">{m.email}</div>
                            </td>
                            <td className="px-4 py-3 text-sm capitalize">{m.status}</td>
                            <td className="px-4 py-3 text-sm capitalize">{m.role}</td>
                            <td className="px-4 py-3 text-sm">
                              <div className="flex gap-2">
                                {m.userId !== user?.id && m.status === 'approved' && m.role === 'member' && (
                                  <button disabled={busy} onClick={() => act(() => updateMemberStatus(selectedGroupId, m.userId, 'approved', 'admin'), () => loadMembers(selectedGroupId), 'Promoted to admin')} className="px-2 py-1 text-xs border border-input rounded disabled:opacity-50">Make admin</button>
                                )}
                                {m.userId !== user?.id && m.role === 'admin' && (
                                  <button disabled={busy} onClick={() => act(() => updateMemberStatus(selectedGroupId, m.userId, 'approved', 'member'), () => loadMembers(selectedGroupId), 'Demoted to member')} className="px-2 py-1 text-xs border border-input rounded disabled:opacity-50">Make member</button>
                                )}
                                {m.userId !== user?.id && (
                                  <button disabled={busy} onClick={() => act(() => removeMember(selectedGroupId, m.userId), () => loadMembers(selectedGroupId), 'Removed')} className="px-2 py-1 text-xs text-destructive border border-destructive/40 rounded disabled:opacity-50">Remove</button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )
          )}

          {/* GROUPS */}
          {tab === 'groups' && (
            <div className="space-y-6">
              <form
                onSubmit={(e) => { e.preventDefault(); if (newName.trim()) act(() => createGroup({ name: newName.trim(), description: newDesc.trim() || undefined }), () => { setNewName(''); setNewDesc(''); refreshTop(); }, 'Group created'); }}
                className="bg-white border border-border rounded-lg p-4 space-y-3"
              >
                <h2 className="font-semibold">Create a group</h2>
                <input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Group name" className="w-full px-3 py-2 border border-input rounded-lg" required />
                <input value={newDesc} onChange={(e) => setNewDesc(e.target.value)} placeholder="Description (optional)" className="w-full px-3 py-2 border border-input rounded-lg" />
                <button type="submit" disabled={busy} className="px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium disabled:opacity-50">Create group</button>
              </form>
              <div>
                <h2 className="font-semibold mb-2">All groups ({allGroups.length})</h2>
                <div className="space-y-2">
                  {allGroups.map((g) => (
                    <div key={g.id} className="p-3 bg-white border border-border rounded-lg">
                      <div className="font-medium">{g.name}</div>
                      {g.description && <div className="text-sm text-muted-foreground">{g.description}</div>}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* USERS (super admin) */}
          {tab === 'users' && isSuper && (
            <div>
              <form onSubmit={(e) => { e.preventDefault(); void loadUsers(); }} className="flex gap-2 mb-4">
                <input value={userSearch} onChange={(e) => setUserSearch(e.target.value)} placeholder="Search by name or email" className="flex-1 px-3 py-2 border border-input rounded-lg" />
                <button type="submit" className="px-4 py-2 border border-input rounded-lg text-sm">Search</button>
              </form>
              <div className="bg-white border border-border rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-secondary"><tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold">User</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Role</th>
                  </tr></thead>
                  <tbody>
                    {users.map((u) => (
                      <tr key={u.id} className="border-t border-border">
                        <td className="px-4 py-3 text-sm">
                          <div className="font-medium">{displayName(u.firstName, u.lastName, u.email)}</div>
                          <div className="text-xs text-muted-foreground">{u.email}</div>
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <select
                            value={u.role}
                            disabled={busy || u.id === user?.id}
                            onChange={(e) => act(() => updateUserRole(u.id, e.target.value as Role), loadUsers, 'Role updated')}
                            className="px-2 py-1 border border-input rounded text-sm disabled:opacity-50"
                          >
                            <option value="superadmin">superadmin</option>
                            <option value="admin">admin</option>
                            <option value="researcher">researcher</option>
                            <option value="viewer">viewer</option>
                          </select>
                        </td>
                      </tr>
                    ))}
                    {users.length === 0 && <tr><td colSpan={2} className="px-4 py-6 text-center text-muted-foreground text-sm">No users loaded. Try searching.</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
